import { store } from './store.js';
/**
 * WIZARD MAIN ORCHESTRATOR
 * Coordina los 6 pasos de la resolución de problemas.
 * Reemplaza al antiguo monolito wizardLogic.js
 */

import { wizardState, getString, normalizeText, extractQuestionFromStatement, getProblemStatement } from './wizardState.js';
import * as Step1 from './wizardStep1.js';
import * as Step2 from './wizardStep2.js';
import * as Step3 from './wizardStep3.js';
import * as Step4 from './wizardStep4.js';
import * as Step5 from './wizardStep5.js';
import * as Step6 from './wizardStep6.js';

// Exponer funciones globales requeridas por el HTML onclicks y otros scripts
window.confirmComprehension = Step1.confirmComprehension;
window.setHighlightMode = Step2.setHighlightMode;
window.highlightSegment = Step2.highlightSegment;
window.clearHighlights = Step2.clearHighlights;
window.validateHighlighting = Step2.validateHighlighting;
window.validateRepresentation = Step3.validateRepresentation;
window.validateLargestData = Step4.validateLargestData;
window.validateMagnitude = Step4.validateMagnitude;
window.validateOperation = Step4.validateOperation;
window.speakReasoningIntro = Step4.speakReasoningIntro;
window.speakSubstep2Intro = Step4.speakSubstep2Intro;
window.speakQuestionReminder = Step4.speakQuestionReminder;
window.checkCalculation = Step5.checkCalculation;
window.openNumberTable = Step5.openNumberTable;
window.closeNumberTable = Step5.closeNumberTable;
window.handleNumberSelection = Step5.handleNumberSelection;
// window.submitAnswer = Step5.submitAnswer; // Removed: invalid export
window.adjustCalcNumber = Step5.adjustCalcNumber;
window.validateCalculation = Step5.validateCalculation;
window.checkCalculationTable = Step5.checkCalculationTable;

// Función de inicialización del Wizard (llamada al iniciar un problema)
// Función de inicialización del Wizard (llamada al iniciar un problema)
window.initializeWizard = function () {
    Logger.info('🧙‍♂️ Inicializando Wizard Modular...');

    // 1. Ocultar modal de éxito por si quedó abierto
    const modal = document.getElementById('problemCompleteModal');
    if (modal) {
        modal.classList.remove('active');
        modal.classList.add('hidden');
    }

    // Reset state
    wizardState.currentWizardStep = 1;
    resetWizardData();

    // Reset UI Indicators
    document.querySelectorAll('.step-indicator').forEach((el) => {
        el.classList.remove('active', 'completed');
        if (el.dataset.step === "1") el.classList.add('active');
    });

    // Reset UI Content Panels
    document.querySelectorAll('.wizard-step').forEach(el => {
        el.classList.add('hidden');
        el.classList.remove('active');
    });

    // 2. ACTUALIZAR UI DE CONTINUIDAD NARRATIVA (STORY MODE)
    updateStoryContinuityUI();

    // Delegar en goToWizardStep para consistencia total (incluye mostrar texto del problema)
    window.goToWizardStep(1);
};
window.resetWizard = window.initializeWizard; // Alias para compatibilidad con problem-logic.js

function updateStoryContinuityUI() {
    const config = store.config;
    const state = store.state;
    if (!config || config.context !== 'cumpleanos' || config.problemType !== 'any' || !state) return;

    // Remove old container if it exists
    let storyContainer = document.getElementById('storyProgressContainer');
    if (!storyContainer) {
        storyContainer = document.createElement('div');
        storyContainer.id = 'storyProgressContainer';
        storyContainer.style.display = 'flex';
        storyContainer.style.justifyContent = 'center';
        storyContainer.style.flexWrap = 'wrap';
        storyContainer.style.gap = '10px';
        storyContainer.style.margin = '15px 0 25px 0';

        const problemScreen = document.getElementById('problemScreen');
        if (!problemScreen) return;

        const header = problemScreen.querySelector('.problem-header');
        if (header && header.parentNode) {
            header.parentNode.insertBefore(storyContainer, header.nextSibling);
        } else {
            problemScreen.prepend(storyContainer);
        }
    }

    // Render steps of the story
    storyContainer.innerHTML = '';

    // 4 pasos basados en el nivel
    let phases = [];
    if (config.level === 'beginner') {
        phases = ['🎈 Parte 1', '💥 Parte 2', '🪑 Parte 3', '🎁 Parte 4'];
    } else if (config.level === 'advanced') {
        phases = ['🎀 Parte 1', '🌬️ Parte 2', '👨‍👩‍👧 Parte 3', '🎁 Parte 4'];

    } else {
        phases = ['📨 Parte 1', '❌ Parte 2', '🥤 Parte 3', '🥳 Parte 4'];
    }

    phases.forEach((phase, index) => {
        const isActive = index === state.currentProblem;
        const isPast = index < state.currentProblem;
        const phaseDiv = document.createElement('div');
        phaseDiv.className = 'story-phase-base';

        if (isActive) {
            phaseDiv.classList.add('story-phase-active');
            phaseDiv.innerHTML = '✨ ' + phase;
        } else if (isPast) {
            phaseDiv.classList.add('story-phase-past');
            phaseDiv.innerHTML = '✅ Finalizado';
        } else {
            phaseDiv.classList.add('story-phase-future');
            phaseDiv.innerHTML = '🔒 Fase ' + (index + 1);
        }

        storyContainer.appendChild(phaseDiv);
    });
}

window.goToWizardStep = function (stepNumber) {
    Logger.info('➡️ Intentando avanzar al paso ' + stepNumber);



    Logger.info('➡️ Avanzando al paso ' + stepNumber);
    
    // Emitir eventos desacoplados y respuesta háptica
    if (window.EventBus) window.EventBus.emit('STEP_CHANGED', { step: stepNumber, previous: wizardState.currentWizardStep });
    if (window.HapticsService) window.HapticsService.selection();

    // UX-05: Confetti celebration when advancing to a new step
    if (stepNumber > wizardState.currentWizardStep && stepNumber > 1 && window.confetti) {
        window.confetti({
            particleCount: 50,
            spread: 60,
            origin: { y: 0.6 }
        });
        if (window.playSuccess) window.playSuccess();
        if (window.HapticsService) window.HapticsService.notification('success');
        
        // Micro-descanso gamificado entre Representación y Razonamiento
        if (stepNumber === 4 && window.DetectiveMentor) {
            const step1Completed = document.querySelector('.step-indicator[data-step="1"]')?.classList.contains('completed');
            const step2Completed = document.querySelector('.step-indicator[data-step="2"]')?.classList.contains('completed');
            const step3Completed = document.querySelector('.step-indicator[data-step="3"]')?.classList.contains('completed');
            
            if (step1Completed && step2Completed && step3Completed) {
                setTimeout(() => {
                    window.DetectiveMentor.speak('¡Excelente representación! Tómate un pequeño respiro antes de pensar en la operación.', 'success');
                }, 1000);
            }
        }
    }
    

    // Apply level classes for hiding steps
    if (stepNumber === 1 && store && store.config) {
        document.body.classList.remove('level-aprendiz', 'level-avanzado', 'level-experto', 'level-beginner', 'level-advanced', 'level-expert');
        if (store.config.level) {
            const levelMap = { 'beginner': 'aprendiz', 'advanced': 'avanzado', 'expert': 'experto' };
            const cssLevel = levelMap[store.config.level] || store.config.level;
            document.body.classList.add('level-' + cssLevel);
        }
    }
    wizardState.currentWizardStep = stepNumber;

    // 0. Actualizar clase en el body para CSS global
    document.body.classList.forEach(className => {
        if (className.startsWith('wizard-step-')) document.body.classList.remove(className);
    });
    document.body.classList.add('wizard-step-' + stepNumber);

    // Prefetch audio for next step to eliminate latency
    if (window.ElevenLabsTTS && typeof window.ElevenLabsTTS.prefetch === 'function') {
        const nextStepIntros = {
            1: "Lee despacio el enunciado. Encuentra las pistas clave.",
            2: "Localiza los datos útiles. ¿Qué números necesitamos para resolver la pregunta?",
            3: "Representa visualmente los datos. Usa las barras para comparar magnitudes.",
            4: "Ahora fíjate en el número mayor y piensa, el resultado será mayor o menor que ese número",
            5: "Elige la operación matemática. ¿Hay que sumar o restar?",
            6: "¡Hora de calcular! Usa la tabla numérica o el rejilla ABN."
        };
        const nextText = nextStepIntros[stepNumber + 1];
        if (nextText) {
            window.ElevenLabsTTS.prefetch(nextText);
        }
    }

    // Actualizar indicadores de pasos
    document.querySelectorAll('.step-indicator').forEach(el => {
        const step = parseInt(el.dataset.step);
        el.classList.remove('active', 'pulse-next');
        if (step === stepNumber) el.classList.add('active');
        
        // Oscurecer pasos futuros
        if (step > stepNumber) {
            el.classList.add('step-future');
        } else {
            el.classList.remove('step-future');
        }
    });

    // Mostrar contenido correspondiente
    document.querySelectorAll('.wizard-step').forEach(el => {
        el.classList.add('hidden');
        el.classList.remove('active');
    });

    const contentId = `wizardStep${stepNumber}`;
    const contentEl = document.getElementById(contentId);
    if (contentEl) {
        contentEl.classList.remove('hidden');
        contentEl.classList.add('active');
    }

    // Inicializar lógica específica del paso
    switch (stepNumber) {
        case 1: Step1.initStep1(); break;
        case 2: Step2.initializeHighlighting(); break;
        case 3: Step3.initializeRepresentation(); break;
        case 4: Step4.initializeReasoningStep(); break;
        case 5: Step5.initializeCalculationStep(); break;
        case 6: Step6.initializeSolutionStep(); break;
    }

    // Añadir botones de audio al contenido recién mostrado
    if (window.addSafeAudioButtons) {
        setTimeout(window.addSafeAudioButtons, 50);
    }

    // Toggle problem statement minimizer
    const problemContainer = document.getElementById('problemTextContainer');
    const problemSection = document.querySelector('.problem-statement-section');
    const toggleBtn = document.getElementById('toggleProblemBtn');

    if (problemContainer) {
        if (stepNumber === 1) {
            // Paso 1: Siempre visible y expandido
            problemContainer.classList.remove('collapsed');
            problemContainer.style.display = 'block'; // Restaurar visualización oculta en Paso 2
            if (problemSection) problemSection.classList.remove('minimized');
            if (toggleBtn) toggleBtn.classList.add('expanded');
        } else {
            // Pasos 2-6: Iniciar colapsado o oculto para focus en la tarea
            if (stepNumber !== 2) {
                problemContainer.style.display = 'block'; // Visible pero colapsado en otros pasos
                problemContainer.classList.add('collapsed');
                if (problemSection) problemSection.classList.add('minimized');
                if (toggleBtn) toggleBtn.classList.remove('expanded');
            }
        }
    }

    // Actualizar mensaje de navegación
    updateNavigationMessage(stepNumber);

    // Scroll al inicio del contenido relevante
    setTimeout(() => {
        if (stepNumber === 1) {
            const problemSection = document.querySelector('.problem-statement-section');
            if (problemSection) problemSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            const wizardEl = document.getElementById('resolutionWizard');
            if (wizardEl) wizardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // AUTO-SPEAK INTRO
        playStepIntro(stepNumber);

    }, 100);
};

function playStepIntro(stepNumber) {
    if (!window.DetectiveMentor) return;

    // MENSAJES AUTOMÁTICOS DEL DETECTIVE (Hardcoded para consistencia)
    let introMessage = "";

    switch (stepNumber) {
        case 1:
            introMessage = "¡Hola detective! Lee el problema con atención. ¿Lo entiendes?";
            break;
        case 2:
            introMessage = "Muy bien. Ahora busca los datos útiles y la pregunta, y subráyalos.";
            break;
        case 3:
            introMessage = "¡Excelente! Es hora de representar los datos con barras para verlo mejor.";
            break;
        case 4:
            introMessage = "Ahora piensa... ¿El resultado será mayor o menor? ¡Elige la operación correcta!";
            break;
        case 5:
            introMessage = "¡Ya casi lo tienes! Elige una estrategia y calcula la solución.";
            break;
        case 6:
            introMessage = "¡Felicidades! Ahora escribe la respuesta completa para cerrar el caso.";
            break;
        default:
            introMessage = "";
    }

    // Añadir animación de latido al siguiente paso
    const nextStepEl = document.querySelector(`.step-indicator[data-step="${stepNumber + 1}"]`);
    if (nextStepEl) {
        nextStepEl.classList.add('pulse-next');
        nextStepEl.classList.remove('step-future'); // Quitar el oscurecimiento para que destaque
    }
}

window.markStepComplete = function (stepNumber) {
    Logger.info(`✅ Paso ${stepNumber} completado`);
    if (window.DynamicAdapter) window.DynamicAdapter.registerAttempt(true, stepNumber);
    const indicator = document.querySelector(`.step-indicator[data-step="${stepNumber}"]`);
    if (indicator) {
        indicator.classList.add('completed');
    }
};

window.showWizardFeedback = function (message, isPositive = true, isConfetti = false, onDismiss = null) {
    Logger.log(`Feedback (${isPositive ? 'Positivo' : 'Negativo'}): ${message}`);

    // Prioritize Detective Mentor Speech Bubble
    if (window.DetectiveMentor && typeof window.DetectiveMentor.speak === 'function') {
        // Multi-turn bugfix: Add small delay to avoid click event bubbling stopping the audio immediately
        setTimeout(() => {
            window.DetectiveMentor.speak(message, isPositive ? 'success' : 'error', 0, onDismiss);
        }, 100);
    }
    // Fallback if Mentor not available
    else if (window.UIManager && window.UIManager.showFeedback) {
        window.UIManager.showFeedback(message, isPositive ? 'success' : 'error');
        // Simple fallback for callback execution
        if (onDismiss && typeof onDismiss === 'function') {
            setTimeout(onDismiss, 3000);
        }
    } else {
        // Last resort
        alert(message);
        if (onDismiss) onDismiss();
    }

    if (isPositive) {
        if (typeof window.playSuccess === 'function') window.playSuccess();
        else if (window.SoundService && window.SoundService.playSFX) window.SoundService.playSFX('success');
    }
    else {
        if (typeof window.playError === 'function') window.playError();
        else if (window.SoundService && window.SoundService.playSFX) window.SoundService.playSFX('error');
        
        // UX-04: If it's an error, highlight the help buttons to guide the user
        document.querySelectorAll('button').forEach(btn => {
            if (btn.textContent.includes('💡') || btn.textContent.toLowerCase().includes('ayuda')) {
                btn.classList.add('pulse-help');
                // Remove pulse after 10 seconds
                setTimeout(() => btn.classList.remove('pulse-help'), 10000);
            }
        });
    }

    if (isConfetti && window.confetti) window.confetti();
};

function updateNavigationMessage(step) {
    const textElement = document.getElementById('stepNavigationText');
    if (!textElement) return;

    let message;
    if (step === 1) {
        message = getString('stepNavStep1');
    } else if (step === 6) {
        message = getString('stepNavLast');
    } else {
        message = getString('stepNavMiddle');
    }

    textElement.textContent = message;
}


function resetWizardData() {
    wizardState.data = {
        comprehension: false,
        highlighting: { question: [], useful: [], distractor: [] },
        usefulDataTexts: [], // Preserve extracted text segments from Step 2
        representation: { bar1: 0, bar2: 0 },
        reasoning: null,
        calculation: null,
        finalAnswer: null,
        errorCounts: {}
    };
}

// Helpers globales para compatibilidad (usados por wizardSteps)
window.getObjectName = function (problem) {
    if (!problem || !problem.statement) return 'elementos';

    const statement = getProblemStatement(problem);

    // STRICT pattern: Matches Noun + (Prep+Noun OR Word2)
    // Word2 can be Adj or Adv. Stops at connectors (y/o).
    const pattern = /\b\d+\s+((?!(?:y|o)\b)[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+)(?:\s+(?:de|del|con|sin|para|en)\s+[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+|\s+(?!(?:y|o)\b)[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+)?\b/g;
    const matches = [...statement.matchAll(pattern)];

    if (matches && matches.length > 0) {
        return matches[0][1].trim();
    }

    return 'objetos';
};

window.getAllObjectNames = function (problem) {
    if (!problem || !problem.statement) return ['unidades'];

    const statement = getProblemStatement(problem);
    // Same robust pattern for all names
    // STRICT pattern: Matches Noun + (Prep+Noun OR Word2)
    const pattern = /\b\d+\s+((?!(?:y|o)\b)[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+)(?:\s+(?:de|del|con|sin|para|en)\s+[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+|\s+(?!(?:y|o)\b)[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+)?\b/g;
    const matches = [...statement.matchAll(pattern)];

    if (matches && matches.length > 0) {
        return matches.map(m => m[1].trim());
    }
    return ['unidades'];
};

/**
 * Extrae las frases COMPLETAS de datos del enunciado (ej: "25 zumos de naranja", "22 de melocotón")
 * Esta función es útil como fallback cuando el niño se salta el paso 2 y no hay textos preservados.
 * @param {Object} problem - El problema actual
 * @returns {Array<string>} - Array de frases completas ordenadas por el valor numérico que contienen
 */
window.getAllDataPhrases = function (problem) {
    if (!problem || !problem.statement) return [];

    // NOU: Priorizar las frases de datos precortadas en Excel/IA si existen
    if (problem.data && problem.data.phrases && Array.isArray(problem.data.phrases) && problem.data.phrases.length > 0) {
        console.log('[getAllDataPhrases] Usando frases precargadas:', problem.data.phrases);
        return problem.data.phrases;
    }

    const statement = getProblemStatement(problem);
    const usefulData = problem.data && problem.data.useful ? problem.data.useful : [];

    // STRICT Data Regex (Sincronizado con wizardStep2.js):
    const dataRegex = /\b(\d+)\s+((?!(?:y|o)\b)[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+)(?:\s+(?:de|del|con|sin|para|en)\s+[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+|\s+(?!(?:y|o)\b)[a-záéíóúñüA-ZÁÉÍÓÚÑÜ]+)?\b/g;

    const allMatches = [];
    let match;
    while ((match = dataRegex.exec(statement)) !== null) {
        if (match[0] && match[0].trim()) {
            allMatches.push({
                text: match[0].trim(),
                num: parseInt(match[1], 10),
                index: match.index
            });
        }
    }

    // Rastreador para no repetir la misma frase si hay números iguales (ej: "5 manzanas y 5 peras")
    const usedMatchIndices = new Set();

    // Mapear cada dato útil a su frase correspondiente en orden de aparición
    const result = usefulData.map(targetNum => {
        const found = allMatches.find(m => m.num === targetNum && !usedMatchIndices.has(m.index));
        if (found) {
            usedMatchIndices.add(found.index);
            return found.text;
        }
        // Fallback: reconstrucción limpia
        const objectNames = window.getAllObjectNames ? window.getAllObjectNames(problem) : [];
        const numIdx = usefulData.indexOf(targetNum);
        const objName = objectNames[numIdx] || 'unidades';
        return `${targetNum} ${objName}`;
    });

    console.log('[getAllDataPhrases] Extracted phrases (Clean & Ordered):', result);
    return result;
};

window.getQuestionElement = function (problem) {
    if (!problem || !problem.statement) return 'elementos';

    const statement = getProblemStatement(problem);

    // Buscar: ¿Cuántos/as + [nombre del objeto] (detener antes de verbos comunes)
    // Patrón mejorado que captura solo el sustantivo, no toda la frase
    const pattern = /¿\s*cuánt[oa]s?\s+([\p{L}\s]+?)(?=\s+(?:hay|tiene|quedan|quedarán|tendrá|habrá|están|van|vienen|llegan|salen|entran|faltan|sobran|necesita|compra|vende|come|bebe|da|recibe|pierde|gana|encuentra|ve|mira|toma|deja|pone|quita|añade|saca|mete|guarda|usa|utiliza|coge|agarra)[^a-záéíóúñü])/iu;
    const match = statement.match(pattern);

    if (match && match[1]) {
        let result = match[1].trim();

        // Remove leading prepositions: "de", "del", "de la", "de los", "de las"
        result = result.replace(/^(de\s+la\s+|de\s+los\s+|de\s+las\s+|del\s+|de\s+)/i, '');

        return result.trim();
    }

    return window.getObjectName(problem);
};

// Utilities export for other modules
window.extractQuestionFromStatement = extractQuestionFromStatement;
window.normalizeText = normalizeText;

// ========================================
// PROBLEM COMPLETION LOGIC
// ========================================

window.finishCurrentProblem = function () {
    Logger.info('✨ Problema completado. Procesando fin de nivel...');

    const problem = store.state.problems[store.state.currentProblem];

    // [SCORM] Enviar puntuación (progresiva según problemas resueltos)
    if (window.ScormManager && store.state.solvedProblemIds) {
        // Asume 10 problemas por sesión como estándar (o usa el length real si existe)
        const total = store.state.problems?.length || 10;
        const solvedCount = store.state.solvedProblemIds.length + 1; // +1 porque el actual aún no se ha guardado en el array abajo
        const scormScore = Math.round((solvedCount / total) * 100);
        window.ScormManager.updateScore(scormScore);
    }

    // 1. Gamification & Telemetry
    if (window.GamificationService) {
        // Assume perfect score for now or calculate failures
        window.GamificationService.completeProblem(problem.type || 'combinada', 0);

        // [NEW] End tracking time and record metric
        if (problem) {
            window.GamificationService.endProblem(
                problem.id || `prob_${Date.now()}`,
                true,
                problem.type || 'combinada',
                problem.level || 1
            );
        }
    }

    // 2. Play Sound
    if (window.SoundService) window.SoundService.playSuccess();
    if (window.confetti) window.confetti();

    // 3. Show Completion Modal
    // Track unique problem completion
    if (problem && problem.id && window.SessionManager) {
        window.SessionManager.recordSolvedProblem(problem.id);
    }

    showCompletionModal();
};

function showCompletionModal() {
    // 1. Obtener datos actuales para el modal
    const progress = window.GamificationService ? window.GamificationService.getProgress() : { level: 1, stars: 0 };
    const island = window.AdventureMap ? window.AdventureMap.getCurrentIsland() : { name: 'Isla del Aprendiz', icon: '🏝️' };

    // 2. Crear o reutilizar modal
    let modal = document.getElementById('problemCompleteModal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'problemCompleteModal';
        document.body.appendChild(modal);
    }

    modal.className = 'modal active'; // Asegurar que sea visible
    modal.style.zIndex = "2000"; // Encima de todo

    modal.innerHTML = `
        <div class="modal-content success-premium-modal animate__animated animate__zoomIn">
            <div class="success-confetti-bg"></div>
            
            <div class="success-header">
                <div class="success-badge">🏆</div>
                <h2 class="high-contrast">¡MISIÓN CUMPLIDA!</h2>
                <div class="success-divider"></div>
            </div>
            
            <div class="rewards-section">
                <div class="reward-card xp-card">
                    <div class="reward-icon">⚡</div>
                    <div class="reward-info">
                        <span class="reward-val">+50</span>
                        <span class="reward-label">PUNTOS XP</span>
                    </div>
                </div>
                <div class="reward-card star-card">
                    <div class="reward-icon">⭐</div>
                    <div class="reward-info">
                        <span class="reward-val">+1</span>
                        <span class="reward-label">ESTRELLA</span>
                    </div>
                </div>
            </div>

            <div class="adventure-progress-card">
                <div class="island-preview">${island.icon}</div>
                <div class="island-details">
                    <p class="progress-label">EXPLORANDO EL MAPA</p>
                    <p class="island-name">${island.name.toUpperCase()}</p>
                    <div class="mini-progress-track">
                        <div class="mini-progress-fill" style="width: ${Math.min(100, (progress.level / 10) * 100)}%"></div>
                    </div>
                </div>
            </div>

            <div class="mentor-final-tip">
                <p>"¡Tu ingenio no tiene límites! Cada problema resuelto te acerca a ser una leyenda de la investigación."</p>
            </div>

            <div class="success-actions-layout">
                <button id="backToHomeBtnModal" class="btn btn-secondary btn-stack">
                    <span class="btn-icon">🏠</span>
                    <span>VOLVER AL INICIO</span>
                </button>
                <button id="goToStoreBtn" class="btn btn-secondary btn-stack">
                    <span class="btn-icon">🏪</span>
                    <span>TIENDA</span>
                </button>
                <button id="goToMapBtn" class="btn btn-secondary btn-stack">
                    <span class="btn-icon">🗺️</span>
                    <span>MAPA</span>
                </button>
                <button id="nextProblemBtnModal" class="btn btn-primary btn-stack">
                    <span class="btn-icon">🚀</span>
                    <span>NUEVO PROBLEMA</span>
                </button>
            </div>
        </div>
    `;

    // 3. Vincular Eventos
    const btnNext = document.getElementById('nextProblemBtnModal');
    const btnStore = document.getElementById('goToStoreBtn');
    const btnMap = document.getElementById('goToMapBtn');
    const btnHome = document.getElementById('backToHomeBtnModal');

    if (btnHome) {
        btnHome.onclick = () => {
            modal.classList.add('hidden');
            modal.classList.remove('active');
            if (typeof window.showWelcomeScreen === 'function') {
                window.showWelcomeScreen();
            } else {
                location.reload();
            }
        };
    }

    if (btnNext) {
        btnNext.onclick = () => {
            modal.classList.add('hidden');
            modal.classList.remove('active');
            window.loadNextProblem();
        };
    }

    if (btnStore) {
        btnStore.onclick = (e) => {
            if (e) e.stopPropagation();
            console.log('🏪 [STORE BTN] Click detectado');

            if (!window.StickerStore) {
                console.error('❌ StickerStore no existe');
                return;
            }

            // PASO 1: Ocultar el modal inmediatamente
            modal.classList.add('hidden');
            modal.classList.remove('active');
            modal.style.display = 'none';
            console.log('✅ Modal ocultado');

            // PASO 2: Abrir la tienda con callback de retorno
            window.StickerStore.openStore(() => {
                console.log('🔄 [STORE BTN] Callback de cierre ejecutado');

                // Restaurar modal
                modal.classList.remove('hidden');
                modal.classList.add('active');
                modal.style.display = 'flex';
                modal.style.zIndex = '9999'; // Muy alto para asegurar visibilidad

                console.log('✅ Modal restaurado');
            });

            // PASO 3: Asegurar que la tienda sea visible (después de que openStore la cree)
            setTimeout(() => {
                const storeScreen = document.getElementById('storeScreen');
                if (storeScreen) {
                    // Forzar visibilidad total
                    storeScreen.classList.remove('hidden');
                    storeScreen.classList.add('active');
                    storeScreen.style.display = 'block';
                    storeScreen.style.zIndex = '10000'; // Más alto que todo
                    storeScreen.style.position = 'fixed';
                    storeScreen.style.top = '0';
                    storeScreen.style.left = '0';
                    storeScreen.style.width = '100%';
                    storeScreen.style.height = '100%';
                    console.log('✅ Tienda forzada a visible con z-index 10000');
                } else {
                    console.error('❌ storeScreen no encontrado');
                }
            }, 100);
        };
    }

    if (btnMap) {
        btnMap.onclick = () => {
            modal.classList.add('hidden');
            modal.classList.remove('active');
            // Pass callback to restore this modal when map closes
            if (window.showMapScreen) {
                window.showMapScreen(() => {
                    // RESTORE BACKDROP: Show problem screen again
                    if (window.SessionManager) window.SessionManager.showProblemScreen();

                    modal.classList.remove('hidden');
                    modal.classList.add('active');
                    // Add z-index again just in case
                    modal.style.zIndex = "2000";
                });
            }
        };
    }

    // 4. Audio Feedback
    if (window.speak) {
        window.speak(window.getString ? window.getString('missionComplete') : "¡Misión cumplida! Has ganado cincuenta puntos de experiencia y una estrella.", window.currentLanguage || "es-ES");
    }
}

window.loadNextProblem = function () {
    // Explicitly reset wizard data to clear previous problem progress immediately
    resetWizardData();

    // INTERCEPTAR PARA CONTINUIDAD NARRATIVA (STORY MODE - CUMPLEAÑOS)
    if (store.config && store.config.context === 'cumpleanos' && store.config.problemType === 'any') {
        if (store.state.currentProblem < store.state.problems.length - 1) {
            // Avanzar al siguiente problema de la misma historia
            store.state.currentProblem++;

            // Re-render UI and initialize
            if (window.displayProblem) window.displayProblem(store.state.currentProblem);
            if (window.UIManager && typeof window.UIManager.renderProblemScreen === 'function') {
                window.UIManager.renderProblemScreen();
            }
            window.initializeWizard();

            // Go to problem screen
            if (window.SessionManager) window.SessionManager.showProblemScreen();
            console.log('📖 [Story Mode] Avanzando al problema ' + (store.state.currentProblem + 1));
            return;
        } else {
            console.log('📖 [Story Mode] Historia completada. Mostrando resumen.');
            if (window.SessionManager) window.SessionManager.showSummaryScreen();
            return;
        }
    }

    // User Request: Volver a la pantalla de elección de operación tras cada problema.
    // Esto permite cambiar de suma a resta o cambiar de contexto en cada paso.

    // Ensure all screens are hidden to prevent "phantom" steps
    if (window.hideAllScreens) window.hideAllScreens();

    if (window.showConfigScreen) {
        window.showConfigScreen();

        // Asegurar que estamos en la selección de tipo de problema (Paso 1)
        // NOTA: window.goToStep aquí se refiere al paso de CONFIGURACIÓN (config-manager.js), no al wizard de problemas
        if (window.goToStep) window.goToStep(1);
    } else {
        console.error('❌ showConfigScreen no está disponible');
        // Fallback
        window.location.reload();
    }
};
window.showCompletionModal = showCompletionModal;

Logger.info('✅ Módulos del Wizard cargados exitosamente');

window.routeNextWizardStep = function(currentStep) {
    if (!window.store || !window.store.config) {
        window.goToWizardStep(currentStep + 1);
        return;
    }
    
    // Normalizar el nivel a su versión en español
    const rawLevel = window.store.config.level || 'aprendiz';
    const levelMap = { 'beginner': 'aprendiz', 'advanced': 'avanzado', 'expert': 'experto' };
    const level = levelMap[rawLevel] || rawLevel;
    
    let next = currentStep + 1;
    
    if (level === 'experto') {
        if (currentStep === 1) {
            const askedForHelp = window.store?.state?.answers?.requestedHelpInStep1;
            next = askedForHelp ? 2 : 5;
        }
    } else if (level === 'avanzado') {
        if (currentStep === 1) {
            const askedForHelp = window.store?.state?.answers?.requestedHelpInStep1;
            next = askedForHelp ? 2 : 4;
        }
    }
    
    window.goToWizardStep(next);
};
